import { FunctionComponent } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useStore } from "@nanostores/preact";
import { useLocation } from "wouter-preact";
import { useTranslator } from "../../hooks/i18n.ts";
import { $cameraList, $cameraListLoaded, updateCameraInfo, invalidateCameraInfo, type CameraInfo } from "../../store/cameras.ts";
import { aiConfigResource, refreshOverviewPage } from "../../store/ai.ts";
import { usePageRefresh } from "../../hooks/use_page_refresh.ts";
import { $isLogined, $userInfo, $username } from "../../store/user_info.ts";
import { gotoLoginPage, navigate } from "../../router_utils.ts";
import { PhotoImage } from "../../components/photo_image/photo_image.tsx";
import { useToast } from "../../components/toast/toast.tsx";
import { APIRequestError, requestGet, requestPost } from "../../utils/request.ts";
import { Icon } from "../../components/icon/icon.tsx";
import { PageHeader } from "../../components/ui/page_header.tsx";
import { AIActivityChart } from "../../components/ai_chart/ai_activity_chart.tsx";
import { getPhotoImageData, getPhotoImageURL, getPhotos } from "../../store/task/photos.ts";
import { useDialog } from "../../components/dialog/dialog.tsx";
import { createPayment, getProducts, getPaymentMethods, type SubscriptionProduct } from "../../api/payment.ts";
import QRCode from "qrcode";

const REPLAY_FRAME_COUNT = 20;
const REPLAY_BLOB_CACHE_LIMIT = REPLAY_FRAME_COUNT;
type ReplayFrameUrl = { kind: "blob" | "url"; value: string };
const replayFrameUrlCache = new Map<number, ReplayFrameUrl>();
const replayBlobUrlOrder: number[] = [];
const REPLAY_HOLD_MOVE_CANCEL_PX = 10;

const touchReplayBlobUrl = (photoId: number) => {
    const idx = replayBlobUrlOrder.indexOf(photoId);
    if (idx >= 0) replayBlobUrlOrder.splice(idx, 1);
    replayBlobUrlOrder.push(photoId);
};

const setReplayFrameUrl = (photoId: number, entry: ReplayFrameUrl) => {
    if (replayFrameUrlCache.has(photoId)) {
        touchReplayBlobUrl(photoId);
        return;
    }
    replayFrameUrlCache.set(photoId, entry);
    replayBlobUrlOrder.push(photoId);
    while (replayBlobUrlOrder.length > REPLAY_BLOB_CACHE_LIMIT) {
        const oldId = replayBlobUrlOrder.shift();
        if (oldId === undefined) break;
        const oldEntry = replayFrameUrlCache.get(oldId);
        if (oldEntry && oldEntry.kind === "blob") URL.revokeObjectURL(oldEntry.value);
        replayFrameUrlCache.delete(oldId);
    }
};

const REPLAY_MUSIC_VOLUME = 0.35;
let replayMusicTracks: string[] = [];
let replayMusicTracksPromise: Promise<string[]> | null = null;
let currentReplayAudio: HTMLAudioElement | null = null;

const stopCurrentReplayAudio = () => {
    if (!currentReplayAudio) return;
    try {
        currentReplayAudio.pause();
        currentReplayAudio.currentTime = 0;
    } catch {
    }
    currentReplayAudio = null;
};

const ensureReplayMusicTracks = async () => {
    if (replayMusicTracksPromise) return replayMusicTracksPromise;
    replayMusicTracksPromise = (async () => {
        try {
            const resp = await requestGet<{ tracks?: string[] }>("/v2/music_list", {});
            replayMusicTracks = (resp.tracks ?? []).filter((x) => typeof x === "string" && x.length > 0);
        } catch {
            replayMusicTracks = [];
        }
        return replayMusicTracks;
    })();
    return replayMusicTracksPromise;
};

const primeReplayMusic = () => {
    stopCurrentReplayAudio();
    if (replayMusicTracks.length <= 0) {
        void ensureReplayMusicTracks();
        return;
    }
    const idx = Math.floor(Math.random() * replayMusicTracks.length);
    const url = replayMusicTracks[idx];
    const audio = new Audio(url);
    audio.loop = true;
    audio.preload = "auto";
    audio.volume = 0;
    currentReplayAudio = audio;
    void audio.play().catch(() => {});
};

const activateReplayMusic = () => {
    if (!currentReplayAudio) return;
    currentReplayAudio.volume = REPLAY_MUSIC_VOLUME;
    void currentReplayAudio.play().catch(() => {});
};

// New V3 Components
const StatBox = ({ title, value, sub, accent, icon, onClick }: { title: string; value: string | number; sub?: string; accent?: "success" | "warning" | "info"; icon?: string; onClick?: () => void }) => (
    <div class={`farpov_stat_box ${accent ? `accent-${accent}` : ""} ${onClick ? "clickable" : ""}`} onClick={onClick} style={onClick ? { cursor: "pointer" } : {}}>
        <div class="farpov_stat_content_left">
            <div class="farpov_stat_header">{title}</div>
            <div class="farpov_stat_value">{value}</div>
            {sub && <div class="farpov_stat_sub">{sub}</div>}
        </div>
        {icon && (
            <div class="farpov_stat_icon_right">
                <Icon name={icon as any} />
            </div>
        )}
    </div>
);

const CameraCardV3 = ({ 
    camera, 
    onPin, 
    onViewPhotos, 
    onSetting, 
    onManage,
    onMakeVideo,
    onComparePhoto,
    t 
}: { 
    camera: CameraInfo; 
    onPin: (e: Event, id: number, pin: boolean) => void;
    onViewPhotos: (id: number) => void;
    onSetting: (id: number) => void;
    onManage: (id: number) => void;
    onMakeVideo: (id: number) => void;
    onComparePhoto: (id: number) => void;
    t: any;
}) => {
    const toast = useToast();
    const isOnline = (Date.now() / 1000 - camera.last_photo_time) < 3600;
    const lastTime = camera.last_photo_time > 0 ? new Date(camera.last_photo_time * 1000).toLocaleString() : t({ zh: "无记录", en: "No Record" });
    const status = camera.status || {};
    const isCharging = String(status["is_charging"] || "").toLowerCase() === "true";
    const [isReplaying, setIsReplaying] = useState(false);
    const [replayUrl, setReplayUrl] = useState("");
    const holdTimeoutIdRef = useRef<number | null>(null);
    const intervalIdRef = useRef<number | null>(null);
    const suppressClickRef = useRef(false);
    const replaySeqRef = useRef<number[]>([]);
    const replayIdxRef = useRef(0);
    const replaySessionRef = useRef(0);
    const isReplayingRef = useRef(false);
    const holdDownRef = useRef<{ x: number; y: number; pointerId: number } | null>(null);
    const holdTargetRef = useRef<HTMLElement | null>(null);
    const holdPointerIdRef = useRef<number | null>(null);
    const replayDirectUrlMapRef = useRef<Record<number, string>>({});

    const stopReplay = (opts?: { suppressClick?: boolean }) => {
        replaySessionRef.current += 1;
        isReplayingRef.current = false;
        stopCurrentReplayAudio();
        if (holdTimeoutIdRef.current !== null) {
            window.clearTimeout(holdTimeoutIdRef.current);
            holdTimeoutIdRef.current = null;
        }
        if (intervalIdRef.current !== null) {
            window.clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
        }
        replaySeqRef.current = [];
        replayIdxRef.current = 0;
        replayDirectUrlMapRef.current = {};
        setIsReplaying(false);
        setReplayUrl("");
        suppressClickRef.current = opts?.suppressClick ?? suppressClickRef.current;
    };

    const startReplay = async () => {
        if (camera.last_photo_id <= 0) return;
        const session = replaySessionRef.current + 1;
        replaySessionRef.current = session;
        suppressClickRef.current = true;
        isReplayingRef.current = true;
        setIsReplaying(true);
        activateReplayMusic();
        try {
            const photos = await getPhotos(camera.camera_id, undefined, undefined, REPLAY_FRAME_COUNT, false);
            if (replaySessionRef.current !== session) return;
            const seq = photos.map((p) => p.photo_id).reverse().filter((id) => id > 0).slice(-REPLAY_FRAME_COUNT);
            if (seq.length <= 0) {
                stopReplay({ suppressClick: true });
                return;
            }

            replaySeqRef.current = seq;
            replayIdxRef.current = 0;
            try {
                const resp = await requestGet<{ photos: { photo_id: number; photo_url: string }[] }>("/v2/photo_img", {
                    photo_id: seq.join(","),
                    mode: "thumbnail",
                });
                if (replaySessionRef.current !== session) return;
                const urlById: Record<number, string> = {};
                for (const p of resp.photos ?? []) {
                    urlById[p.photo_id] = p.photo_url;
                }
                replayDirectUrlMapRef.current = urlById;
            } catch {
                replayDirectUrlMapRef.current = {};
            }

            const firstUrl = replayDirectUrlMapRef.current[seq[0]] ?? "";
            if (firstUrl) setReplayUrl(firstUrl);

            const prefetchIds = seq.slice(1, 13);
            void (async () => {
                let i = 0;
                const concurrency = 3;
                const worker = async () => {
                    while (i < prefetchIds.length && replaySessionRef.current === session) {
                        const id = prefetchIds[i];
                        i += 1;
                        try {
                            const buffer = await getPhotoImageData(id, "thumbnail");
                            if (replaySessionRef.current !== session) return;
                            const url = URL.createObjectURL(new Blob([buffer]));
                            setReplayFrameUrl(id, { kind: "blob", value: url });
                            if (replaySeqRef.current[replayIdxRef.current] === id) {
                                setReplayUrl(url);
                            }
                        } catch {
                        }
                    }
                };
                await Promise.all(Array.from({ length: concurrency }, () => worker()));
            })();

            intervalIdRef.current = window.setInterval(() => {
                const s = replaySeqRef.current;
                if (s.length <= 0) return;
                replayIdxRef.current = (replayIdxRef.current + 1) % s.length;
                const id = s[replayIdxRef.current];
                const cached = replayFrameUrlCache.get(id);
                if (cached) {
                    touchReplayBlobUrl(id);
                    setReplayUrl(cached.value);
                    return;
                }
                const url = replayDirectUrlMapRef.current[id] ?? "";
                if (url) setReplayUrl(url);
            }, 140);
        } catch (err) {
            const detail = err instanceof APIRequestError
                ? `${err.statusCode} ${err.message}`
                : (err instanceof Error ? err.message : "Unknown");
            toast(t({ zh: `轮播失败：${detail}`, en: `Replay failed: ${detail}` }), "error");
            stopReplay({ suppressClick: true });
        }
    };

    useEffect(() => {
        return () => stopReplay({ suppressClick: false });
    }, []);

    return (
        <div class="farpov_camera_card_v3">
            <div class="farpov_cam_preview_wrap" onClick={() => onViewPhotos(camera.camera_id)}>
                {camera.last_photo_id > 0 ? (
                    (isReplaying && replayUrl)
                        ? <img decoding="async" referrerpolicy="no-referrer" src={replayUrl} />
                        : <PhotoImage photoId={camera.last_photo_id} mode="thumbnail" />
                ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#222", color: "#555" }}>
                        <Icon name="image" size={32} />
                    </div>
                )}
                {camera.camera_pinned_time >= 0 && (
                    <div class="farpov_pin_badge" title={t({ zh: "已置顶", en: "Pinned" })}>
                        <Icon name="paperclip" size={14} />
                    </div>
                )}
                <div class="farpov_cam_overlay">
                    <div class="farpov_cam_status">
                        <span class={`status_indicator ${isOnline ? "on" : "off"}`}></span>
                        <span style={{ marginLeft: "0.35rem" }}>{camera.capture_interval || "--"}</span>
                        <span style={{ margin: "0 0.3rem", opacity: 0.6 }}>·</span>
                        <span>{camera.photo_count.toLocaleString()} {t({ zh: "张", en: "Pic" })}</span>
                    </div>
                    <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.8)" }}>
                        {lastTime}
                    </div>
                </div>
            </div>
            <div class="farpov_cam_meta">
                <div class="farpov_cam_header">
                    <h5 class="farpov_cam_title" title={camera.camera_name}>{camera.camera_name}</h5>
                    <button 
                        class="outline secondary" 
                        style={{ padding: "0.2rem", border: "none", marginBottom: 0 }}
                        onClick={(e) => onPin(e, camera.camera_id, camera.camera_pinned_time < 0)}
                        title={camera.camera_pinned_time >= 0 ? t({ zh: "取消置顶", en: "Unpin" }) : t({ zh: "置顶", en: "Pin" })}
                    >
                        <Icon name="paperclip" size={14} style={{ opacity: camera.camera_pinned_time >= 0 ? 1 : 0.3 }} />
                    </button>
                </div>
                
                {/* Hardware Status Tags */}
                <div class="farpov_cam_hw_status">
                    {status["battery"] && (
                        <div 
                            class="farpov_hw_tag" 
                            title={t({ 
                                zh: `电量${isCharging ? " (充电中)" : ""}`, 
                                en: `Battery${isCharging ? " (Charging)" : ""}` 
                            })}
                        >
                            <Icon name={isCharging ? "battery-charging" : "battery"} /> {status["battery"]}
                        </div>
                    )}
                    {status["cpu_temperature"] && (
                        <div class="farpov_hw_tag" title={t({ zh: "CPU 温度", en: "CPU Temp" })}>
                            <Icon name="thermometer" /> {status["cpu_temperature"]}
                        </div>
                    )}
                    {status["cpu_usage"] && (
                        <div class="farpov_hw_tag" title={t({ zh: "CPU 使用率", en: "CPU Usage" })}>
                            <Icon name="activity" /> {status["cpu_usage"]}
                        </div>
                    )}
                    {status["hard_drive_remain"] && (
                        <div class="farpov_hw_tag" title={t({ zh: "剩余空间", en: "Storage" })}>
                            <Icon name="hard-drive" /> {status["hard_drive_remain"]}
                        </div>
                    )}
                </div>

                <div class="farpov_cam_actions">
                    <button
                        class="farpov_cam_btn primary"
                        onPointerDown={(e) => {
                            e.stopPropagation();
                            suppressClickRef.current = false;
                            holdDownRef.current = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
                            holdTargetRef.current = e.currentTarget as HTMLElement;
                            holdPointerIdRef.current = e.pointerId;
                            primeReplayMusic();
                            if (holdTimeoutIdRef.current !== null) {
                                window.clearTimeout(holdTimeoutIdRef.current);
                            }
                            holdTimeoutIdRef.current = window.setTimeout(() => {
                                holdTimeoutIdRef.current = null;
                                const el = holdTargetRef.current;
                                const pid = holdPointerIdRef.current;
                                if (el && pid !== null) {
                                    try {
                                        el.setPointerCapture(pid);
                                    } catch {
                                    }
                                }
                                startReplay();
                            }, 220);
                        }}
                        onPointerMove={(e) => {
                            if (holdTimeoutIdRef.current === null) return;
                            const down = holdDownRef.current;
                            if (!down) return;
                            if (down.pointerId !== e.pointerId) return;
                            const dx = e.clientX - down.x;
                            const dy = e.clientY - down.y;
                            if (Math.hypot(dx, dy) < REPLAY_HOLD_MOVE_CANCEL_PX) return;
                            window.clearTimeout(holdTimeoutIdRef.current);
                            holdTimeoutIdRef.current = null;
                            holdDownRef.current = null;
                            holdTargetRef.current = null;
                            holdPointerIdRef.current = null;
                            stopCurrentReplayAudio();
                            suppressClickRef.current = false;
                        }}
                        onPointerUp={(e) => {
                            // e.preventDefault(); // Removed to allow click event on mobile
                            e.stopPropagation();
                            if (holdTimeoutIdRef.current !== null) {
                                window.clearTimeout(holdTimeoutIdRef.current);
                                holdTimeoutIdRef.current = null;
                                holdDownRef.current = null;
                                holdTargetRef.current = null;
                                holdPointerIdRef.current = null;
                                stopCurrentReplayAudio();
                                suppressClickRef.current = false;
                                return;
                            }
                            if (isReplayingRef.current) {
                                stopReplay({ suppressClick: true });
                            }
                            try {
                                (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
                            } catch {
                            }
                        }}
                        onPointerCancel={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (holdTimeoutIdRef.current !== null) {
                                window.clearTimeout(holdTimeoutIdRef.current);
                                holdTimeoutIdRef.current = null;
                                holdDownRef.current = null;
                                holdTargetRef.current = null;
                                holdPointerIdRef.current = null;
                                stopCurrentReplayAudio();
                                suppressClickRef.current = false;
                            }
                            if (isReplayingRef.current) {
                                stopReplay({ suppressClick: true });
                            }
                        }}
                        onPointerLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (holdTimeoutIdRef.current !== null) {
                                window.clearTimeout(holdTimeoutIdRef.current);
                                holdTimeoutIdRef.current = null;
                                holdDownRef.current = null;
                                holdTargetRef.current = null;
                                holdPointerIdRef.current = null;
                                stopCurrentReplayAudio();
                                suppressClickRef.current = false;
                            }
                            if (isReplayingRef.current) {
                                stopReplay({ suppressClick: true });
                            }
                        }}
                        onLostPointerCapture={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (holdTimeoutIdRef.current !== null) {
                                window.clearTimeout(holdTimeoutIdRef.current);
                                holdTimeoutIdRef.current = null;
                            }
                            holdDownRef.current = null;
                            holdTargetRef.current = null;
                            holdPointerIdRef.current = null;
                            stopCurrentReplayAudio();
                            if (isReplayingRef.current) {
                                stopReplay({ suppressClick: true });
                            }
                        }}
                        onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (holdTimeoutIdRef.current !== null) {
                                window.clearTimeout(holdTimeoutIdRef.current);
                                holdTimeoutIdRef.current = null;
                            }
                            holdDownRef.current = null;
                            holdTargetRef.current = null;
                            holdPointerIdRef.current = null;
                            stopCurrentReplayAudio();
                            if (isReplayingRef.current) {
                                stopReplay({ suppressClick: true });
                            }
                            suppressClickRef.current = true;
                        }}
                        onClick={(e) => {
                            if (isReplayingRef.current) {
                                e.preventDefault();
                                e.stopPropagation();
                                stopReplay({ suppressClick: true });
                                return;
                            }
                            if (suppressClickRef.current) {
                                e.preventDefault();
                                e.stopPropagation();
                                suppressClickRef.current = false;
                                return;
                            }
                            onViewPhotos(camera.camera_id);
                        }}
                    >
                        <Icon name="eye" size={14} /> {t({ zh: "查看", en: "View" })}
                    </button>
                    <button class="farpov_cam_btn" onClick={() => onMakeVideo(camera.camera_id)} title={t({ zh: "生成视频", en: "Make Video" })}>
                        <Icon name="film" size={14} />
                    </button>
                    <button class="farpov_cam_btn" onClick={() => onComparePhoto(camera.camera_id)} title={t({ zh: "照片对比", en: "Compare" })}>
                        <Icon name="columns" size={14} />
                    </button>
                    <button class="farpov_cam_btn" onClick={() => onSetting(camera.camera_id)} title={t({ zh: "设置", en: "Settings" })}>
                        <Icon name="settings" size={14} />
                    </button>
                    <button class="farpov_cam_btn" onClick={() => onManage(camera.camera_id)} title={t({ zh: "管理", en: "Manage" })}>
                        <Icon name="edit" size={14} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export const OverviewPage: FunctionComponent = (_) => {
    const t = useTranslator();
    const toast = useToast();
    const isLogined = useStore($isLogined);
    const cameraList = useStore($cameraList);
    const cameraListLoaded = useStore($cameraListLoaded);
    const userInfo = useStore($userInfo);
    const username = useStore($username);
    const [location, _setLocation] = useLocation();
    const [isBusy, setIsBusy] = useState(false);
    const aiConfig = useStore(aiConfigResource.$state);
    const showAITrend = !!aiConfig.data?.ai_enabled && !!aiConfig.data?.show_overview_ai_trend;
    const trendDays = aiConfig.data?.preferred_trend_days ?? 7;
    const pageRefresh = usePageRefresh({
        pageKey: "overview", enabled: isLogined, refresh: refreshOverviewPage, canAutoRefresh: !isBusy,
    });
    const [memberProducts, setMemberProducts] = useState<SubscriptionProduct[]>([]);
    const [selectedMemberProductId, setSelectedMemberProductId] = useState<number>(0);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('alipay');
    const [memberPaymentMethods, setMemberPaymentMethods] = useState<string[]>([]);
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');

    useEffect(() => {
        if (isLogined) {
            getProducts().then(res => {
                if (res.products && res.products.length > 0) {
                    const userProducts = res.products.filter((p) => p.product_type === "user" || p.product_type === "member");
                    setMemberProducts(userProducts);
                    if (userProducts.length > 0) {
                        setSelectedMemberProductId(userProducts[0].id);
                    }
                }
            }).catch(() => {});
        }
    }, [isLogined]);

    useEffect(() => {
        if (!isLogined) return;
        getPaymentMethods().then(res => {
            const methods = res.methods || [];
            setMemberPaymentMethods(methods);
            if (methods.length > 0) {
                setSelectedPaymentMethod(methods[0]);
            }
        }).catch(() => {});
    }, [isLogined]);

    const memberSubDialog = useDialog({
        title: t({ zh: "会员续费", en: "Membership Renewal" }),
        showCloseButton: true,
        children: (
            <div style={{ padding: "1rem" }}>
                {qrCodeDataUrl ? (
                    <div style={{ textAlign: "center" }}>
                        <p>{t({ zh: "请使用微信扫描二维码支付", en: "Please scan QR code with WeChat" })}</p>
                        <img src={qrCodeDataUrl} alt="Payment QR Code" style={{ width: "200px", height: "200px" }} />
                        <div style={{ marginTop: "1rem" }}>
                             <button onClick={() => {
                                 setQrCodeDataUrl('');
                                 memberSubDialog.close();
                                 void pageRefresh.refreshNow();
                             }}>
                                 {t({ zh: "完成支付", en: "Done" })}
                             </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <p>
                            {t({ zh: "当前账户", en: "Account" })}: <strong>{username}</strong>
                        </p>
                        {memberProducts.length > 0 ? (
                            <>
                                <label>
                                    {t({ zh: "选择套餐", en: "Select Plan" })}
                                    <select 
                                        value={selectedMemberProductId} 
                                        onChange={(e) => {
                                            setSelectedMemberProductId(parseInt(e.currentTarget.value));
                                        }}
                                    >
                                        {memberProducts.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name} - {p.currency === 'CNY' ? '¥' : '$'}{p.price} ({p.duration_days} {t({ zh: "天", en: "Days" })})
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {memberPaymentMethods.length > 0 ? (
                                <label>
                                    {t({ zh: "支付方式", en: "Payment Method" })}
                                    <select 
                                        value={selectedPaymentMethod} 
                                        onChange={(e) => setSelectedPaymentMethod(e.currentTarget.value)}
                                    >
                                        {memberPaymentMethods.map((m) => (
                                            <option key={m} value={m}>
                                                {m === "alipay" ? t({ zh: "支付宝", en: "Alipay" })
                                                    : m === "wechat" ? t({ zh: "微信支付", en: "WeChat Pay" })
                                                    : m === "paypal" ? t({ zh: "PayPal", en: "PayPal" })
                                                    : m}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                ) : (
                                    <p style={{ color: "red" }}>{t({ zh: "暂无可用支付方式，请联系管理员", en: "No payment methods available. Contact admin." })}</p>
                                )}
                                <p>
                                    {t({ zh: "描述", en: "Description" })}: {memberProducts.find(p => p.id === selectedMemberProductId)?.description || "-"}
                                </p>
                            </>
                        ) : (
                            <p style={{ color: "red" }}>{t({ zh: "暂无可用会员套餐，请联系管理员", en: "No plans available, contact admin." })}</p>
                        )}
                    </>
                )}
            </div>
        ),
        onConfirm: async () => {
            if (qrCodeDataUrl) {
                setQrCodeDataUrl('');
                memberSubDialog.close();
                void pageRefresh.refreshNow();
                return;
            }

            if (!selectedMemberProductId) return;
            if (memberPaymentMethods.length === 0) {
                alert(t({ zh: "暂无可用支付方式，请联系管理员", en: "No payment methods available. Contact admin." }));
                memberSubDialog.close();
                return;
            }
            let shouldClose = false;

            try {
                // Check for WeChat code
                const searchParams = new URLSearchParams(window.location.search);
                const code = searchParams.get('code') || undefined;
                
                // Pass null as cameraId for user subscription
                const res = await createPayment(null, selectedMemberProductId, selectedPaymentMethod, code);
                
                if (res.need_oauth && res.appid) {
                    const currentUrl = window.location.href;
                    const urlObj = new URL(currentUrl);
                    urlObj.searchParams.delete('code');
                    urlObj.searchParams.delete('state');
                    const redirectUri = encodeURIComponent(urlObj.toString());
                    const appId = res.appid;
                    const oauthUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_base&state=STATE#wechat_redirect`;
                    window.location.href = oauthUrl;
                    return;
                }
                
                if (res.type === 'jsapi' && res.jsapi_params) {
                     const onBridgeReady = () => {
                        (window as any).WeixinJSBridge.invoke(
                            'getBrandWCPayRequest', res.jsapi_params,
                            function(res: any){
                                if(res.err_msg == "get_brand_wcpay_request:ok" ) {
                                    alert(t({ zh: "支付成功", en: "Payment Successful" }));
                                    window.location.reload();
                                }
                            }
                        );
                    };

                    if (typeof (window as any).WeixinJSBridge == "undefined"){
                        if( document.addEventListener ){
                            document.addEventListener('WeixinJSBridgeReady', onBridgeReady, false);
                        } else if ((document as any).attachEvent){
                            (document as any).attachEvent('WeixinJSBridgeReady', onBridgeReady); 
                            (document as any).attachEvent('onWeixinJSBridgeReady', onBridgeReady);
                        }
                    } else {
                        onBridgeReady();
                    }
                    shouldClose = true;
                } else if (res.code === 200 && res.pay_url) {
                    if (res.type === 'native') {
                        try {
                            const dataUrl = await QRCode.toDataURL(res.pay_url);
                            setQrCodeDataUrl(dataUrl);
                            shouldClose = false;
                        } catch (err) {
                            console.error("QR Code generation failed", err);
                            alert(t({ zh: "生成二维码失败", en: "Failed to generate QR Code" }));
                            shouldClose = true;
                        }
                    } else {
                        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                        if (isMobile) {
                            window.location.href = res.pay_url;
                        } else {
                            const newWindow = window.open(res.pay_url, '_blank');
                            if (!newWindow) {
                                window.location.href = res.pay_url;
                            }
                        }
                        shouldClose = true;
                    }
                } else {
                    alert(t({ zh: "创建订单失败: " + (res.msg || "未知错误"), en: "Failed to create order: " + (res.msg || "Unknown error") }));
                    shouldClose = true;
                }
            } catch (e: any) {
                alert(t({ zh: "支付请求错误: " + (e.message || "未知"), en: "Payment request error: " + (e.message || "Unknown") }));
                shouldClose = true;
            }
            
            if (shouldClose) {
                memberSubDialog.close();
            }
        }
    });

    const openMemberSubDialog = () => {
        setQrCodeDataUrl('');
        if (memberProducts.length > 0 && selectedMemberProductId === 0) {
            setSelectedMemberProductId(memberProducts[0].id);
        }
        memberSubDialog.open();
    };

    const todayText = useMemo(() => {
        const now = new Date();
        const dateZh = now.toLocaleDateString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit" });
        const dateEn = now.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
        return t({ zh: `今天是 ${dateZh}`, en: `Today is ${dateEn}` });
    }, [t]);

    useEffect(() => {
        document.title = t({ zh: "仪表盘", en: "Dashboard" });
    }, [t]);

    useEffect(() => {
        void ensureReplayMusicTracks();
    }, []);

    useEffect(() => {
        if (!isLogined) {
            gotoLoginPage(location ?? "/overview");
        }
    }, [isLogined, location]);

    const handleRefresh = pageRefresh.refreshNow;

    const pinAction = useMemo(() => async (e: Event, cameraId: number, pin: boolean) => {
        e.stopPropagation();
        try {
            setIsBusy(true);
            await requestPost("/v2/camera/pin", { camera_id: cameraId, pin });
            invalidateCameraInfo();
            await updateCameraInfo().catch(error =>
                toast(t({ zh: "置顶已更新，数据刷新失败：", en: "Pin updated; refresh failed: " }) + error.message, "error"));
        } catch (e) {
            toast((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            setIsBusy(false);
        }
    }, [toast, setIsBusy]);

    const gotoPhotos = useMemo(() => (cameraId: number) => navigate(`/photos/${cameraId}`, { replace: false }), []);
    const gotoCameraSetting = useMemo(() => (cameraId: number) => navigate(`/camera_setting/${cameraId}`, { replace: false }), []);
    const gotoCameraManager = useMemo(() => (cameraId: number) => navigate(`/camera/${cameraId}`, { replace: false }), []);
    const gotoMakeVideo = useMemo(() => (cameraId: number) => navigate(`/make_video`, { replace: false, state: { cameraId } }), []);
    const gotoComparePhoto = useMemo(() => (cameraId: number) => navigate(`/compare_photo`, { replace: false, state: { cameraId } }), []);

    // Logic: Categorize Cameras
    const pinnedCameras = useMemo(() => cameraList.filter(c => c.camera_pinned_time >= 0), [cameraList]);
    const otherCameras = useMemo(() => cameraList.filter(c => c.camera_pinned_time < 0), [cameraList]);
    
    // Group other cameras by group_name
    const groupedCameras = useMemo(() => {
        const groups: Record<string, CameraInfo[]> = {};
        const noGroup: CameraInfo[] = [];

        otherCameras.forEach(cam => {
            const gName = (cam.group_name || "").trim();
            if (gName) {
                if (!groups[gName]) groups[gName] = [];
                groups[gName].push(cam);
            } else {
                noGroup.push(cam);
            }
        });

        // Sort group names
        const sortedGroupNames = Object.keys(groups).sort();
        
        return { groups, sortedGroupNames, noGroup };
    }, [otherCameras]);

    // Stats
    const stats = useMemo(() => {
        const total = cameraList.length;
        const totalPhotos = cameraList.reduce((acc, c) => acc + c.photo_count, 0);
        const now = Date.now() / 1000;
        const active = cameraList.filter(c => (now - c.last_photo_time) < 3600).length; // 1 hour active
        return { total, totalPhotos, active };
    }, [cameraList]);

    if (!isLogined) return <article aria-busy="true" class="container"></article>;

    return (
        <div class="farpov_top_container farpov_dashboard_v3">
            <PageHeader 
                title={t({ zh: `仪表盘`, en: `Dashboard` })}
                subtitle={`${t({ zh: `欢迎回来, ${username}`, en: `Welcome back, ${username}` })} · ${todayText}`}
                actions={
                    <>
                        <button class="outline" disabled={isBusy || pageRefresh.refreshing} onClick={handleRefresh}>
                            <Icon name="refresh-cw" /> {t({ zh: "刷新", en: "Refresh" })}
                        </button>
                        <button onClick={() => navigate("/camera/new", { replace: false })}>
                            <Icon name="plus" /> {t({ zh: "新增相机", en: "New Camera" })}
                        </button>
                    </>
                }
            />
            {pageRefresh.error && <p role="alert">{t({ zh: "刷新失败：", en: "Refresh failed: " })}{pageRefresh.error}</p>}
            {!cameraListLoaded && pageRefresh.refreshing && <p aria-busy="true">{t({ zh: "加载中...", en: "Loading..." })}</p>}

            {/* 1. Key Metrics Row */}
            <div class="farpov_stats_row">
                <StatBox 
                    title={t({ zh: "活跃设备", en: "Active Devices" })} 
                    value={`${stats.active} / ${stats.total}`} 
                    sub={t({ zh: "过去1小时有活动", en: "Active in last hour" })}
                    accent={stats.active > 0 ? "success" : "warning"}
                    icon="activity"
                />
                <StatBox 
                    title={t({ zh: "照片总量", en: "Total Photos" })} 
                    value={stats.totalPhotos.toLocaleString()} 
                    accent="info"
                    icon="image"
                />
                <StatBox 
                    title={t({ zh: "存储状态", en: "Storage Status" })} 
                    value={t({ zh: "正常", en: "Normal" })} // Mock data for now
                    sub={t({ zh: "OSS 连接正常", en: "OSS Connected" })}
                    icon="hard-drive"
                />
                <StatBox 
                    title={t({ zh: "会员服务", en: "Membership" })} 
                    value={userInfo?.adv_expire && userInfo.adv_expire * 1000 > Date.now() ? "Pro" : "Free"} 
                    sub={userInfo?.adv_expire ? new Date(userInfo.adv_expire * 1000).toLocaleDateString() : t({ zh: "联系厂家升级", en: "Contact to Upgrade" })}
                    icon="user"
                    onClick={() => openMemberSubDialog()}
                />
            </div>

            {showAITrend ? <AIActivityChart days={trendDays} /> : null}

            {/* 2. Pinned Cameras (If any) */}
            {pinnedCameras.length > 0 && (
                <div>
                    <div class="farpov_section_header_v3">
                        <h4 class="farpov_section_title_v3">
                            <Icon name="star" size={18} style={{ color: "var(--pico-primary)" }} />
                            {t({ zh: "重点关注", en: "Pinned Cameras" })}
                        </h4>
                    </div>
                    <div class="farpov_camera_grid_v3">
                        {pinnedCameras.map(cam => (
                            <CameraCardV3 
                                key={cam.camera_id} 
                                camera={cam} 
                                onPin={pinAction} 
                                onViewPhotos={gotoPhotos}
                                onSetting={gotoCameraSetting}
                                onManage={gotoCameraManager}
                                onMakeVideo={gotoMakeVideo}
                                onComparePhoto={gotoComparePhoto}
                                t={t}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* 3. All Cameras (Others) - Grouped */}
            <div>
                {cameraListLoaded && cameraList.length === 0 ? (
                    <div class="farpov_empty_dashboard">
                        <Icon name="camera-off" class="farpov_empty_icon" />
                        <h3>{t({ zh: "暂无相机设备", en: "No Cameras Found" })}</h3>
                        <p>{t({ zh: "点击右上角“新增相机”开始接入您的设备。", en: "Click 'New Camera' to add your first device." })}</p>
                        <button onClick={() => navigate("/camera/new", { replace: false })}>
                            {t({ zh: "立即新增", en: "Create Now" })}
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Display Groups */}
                        {groupedCameras.sortedGroupNames.map(groupName => (
                            <div key={groupName} style={{ marginBottom: "2rem" }}>
                                <div class="farpov_section_header_v3">
                                    <h4 class="farpov_section_title_v3">
                                        <Icon name="folder" size={18} />
                                        {groupName}
                                    </h4>
                                </div>
                                <div class="farpov_camera_grid_v3">
                                    {groupedCameras.groups[groupName].map(cam => (
                                        <CameraCardV3 
                                            key={cam.camera_id} 
                                            camera={cam} 
                                            onPin={pinAction} 
                                            onViewPhotos={gotoPhotos}
                                            onSetting={gotoCameraSetting}
                                            onManage={gotoCameraManager}
                                            onMakeVideo={gotoMakeVideo}
                                            onComparePhoto={gotoComparePhoto}
                                            t={t}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Display Ungrouped */}
                        {groupedCameras.noGroup.length > 0 && (
                            <div>
                                <div class="farpov_section_header_v3">
                                    <h4 class="farpov_section_title_v3">
                                        <Icon name="grid" size={18} />
                                        {pinnedCameras.length > 0 || groupedCameras.sortedGroupNames.length > 0 
                                            ? t({ zh: "其他相机", en: "Other Cameras" }) 
                                            : t({ zh: "所有相机", en: "All Cameras" })}
                                    </h4>
                                </div>
                                <div class="farpov_camera_grid_v3">
                                    {groupedCameras.noGroup.map(cam => (
                                        <CameraCardV3 
                                            key={cam.camera_id} 
                                            camera={cam} 
                                            onPin={pinAction} 
                                            onViewPhotos={gotoPhotos}
                                            onSetting={gotoCameraSetting}
                                            onManage={gotoCameraManager}
                                            onMakeVideo={gotoMakeVideo}
                                            onComparePhoto={gotoComparePhoto}
                                            t={t}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default OverviewPage;
